import * as React from "react";
import { useState, useEffect } from "react";
import AntDTable from "@/common/components/ui/antd-table";
import { Card, CardContent } from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";

import { Separator } from "@/common/components/ui/separator";
import LogsService from "@/services/FinEaseHomeAndAdmin/APIs/LogsService";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
    FileText,
    Search,
    RefreshCw,
    Download,
    Filter,
    ShieldCheck,
    Eye,
    Globe,
    Laptop,
    Activity,
} from "lucide-react";
import CommonModal from "@/common/components/ui/modal";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";

const UserAuditLogs = () => {
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    // Data States
    const [auditLogs, setAuditLogs] = useState<any[]>([]);
    const [selectedLog, setSelectedLog] = useState<any | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    // Pagination States
    const [auditPagination, setAuditPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchLogs = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res: any = await LogsService.userAuditLogsAPI(page, pageSize);
            const data = res.results ? res : res.data;
            if (data?.results) {
                setAuditLogs(data.results);
                setAuditPagination({ current: data.page, pageSize: data.page_size, total: data.count });
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error fetching logs",
                description: error.message ?? "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial Fetch
    useEffect(() => {
        fetchLogs(auditPagination.current, auditPagination.pageSize);
    }, []);

    const handleRefresh = () => {
        fetchLogs(auditPagination.current, auditPagination.pageSize);
        toast({ title: "Refreshed", description: "Audit logs updated." });
    };

    const handlePageChange = (page: number, pageSize: number) => {
        fetchLogs(page, pageSize);
    };

    const formatDate = (dateUnparsed: string) => {
        try {
            return format(new Date(dateUnparsed), "MMM dd, yyyy HH:mm:ss");
        } catch (_) {
            return dateUnparsed;
        }
    };

    const onSelectChange = (keys: React.Key[], rows: any[]) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const handleExport = () => {
        if (selectedRows.length === 0) {
            notify.error("No Data", "Please select items to export.");
            return;
        }

        const rows = selectedRows.map(item => ({
            Timestamp: item.created_on ? format(new Date(item.created_on), "MMM dd, yyyy HH:mm:ss") : "-",
            User: item.user_email ?? "-",
            Action: item.action ?? "-",
            Resource: item.resource_type ?? "-",
            "Resource ID": item.resource_id ?? "-",
            Method: item.request_method ?? "-",
            IP: item.ip_address ?? "-",
            App: item.app_name ?? "-",
            Tenant: item.tenant_name ?? "-",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, "AuditLogs");

        XLSX.writeFile(
            wb,
            `user_audit_logs_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        notify.success("Exported", `${rows.length} record(s) exported.`);
    };

    // Helper to format resource type
    const formatResourceType = (type: string) => {
        return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // Helper to get action color
    const getActionColor = (action: string) => {
        switch (action?.toLowerCase()) {
            case 'create': return 'bg-green-100 text-green-700 border-green-200';
            case 'update': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'delete': return 'bg-red-100 text-red-700 border-red-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    // --- Columns Definitions ---

    const auditColumns = [
        {
            title: "Timestamp",
            dataIndex: "created_on",
            key: "created_on",
            width: 200,
            render: (text: string) => (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-700 text-sm">{formatDate(text)}</span>
                    <span className="text-xs text-muted-foreground">{format(new Date(text), "PP")}</span>
                </div>
            ),
        },
        {
            title: "User Identity",
            key: "user",
            width: 200,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1">
                    <span className="font-medium text-gray-900 text-sm">{record.user_email}</span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1 bg-gray-100 px-1.5 py-0.5 rounded">
                            <Globe className="w-3 h-3" /> {record.ip_address}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: "Event",
            key: "event",
            width: 200,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`uppercase text-[10px] tracking-wider px-2 py-0.5 font-bold ${getActionColor(record.action)}`}>
                            {record.action}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] font-mono text-white">
                            {record.request_method}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Activity className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">{formatResourceType(record.resource_type)}</span>
                        {record.resource_id && (
                            <span className="text-xs font-mono text-gray-400 max-w-[80px] truncate" title={record.resource_id}>
                                #{record.resource_id.substring(0, 8)}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: "Context",
            key: "context",
            width: 200,
            render: (_: any, record: any) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Laptop className="w-3.5 h-3.5 text-gray-400" />
                        <span className="truncate max-w-[150px]">{record.app_name}</span>
                    </div>
                    {record.tenant_name && (
                        <span className="text-[10px] text-gray-400 px-1.5 rounded border w-fit">
                            {record.tenant_name}
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Details",
            key: "actions",
            width: 100,
            align: "right" as const,
            render: (_: any, record: any) => (
                <Button
                    variant="ghost"
                    size="sm"
                    className="hover:bg-blue-50 hover:text-blue-600"
                    onClick={() => setSelectedLog(record)}
                >
                    <Eye className="w-4 h-4 mr-1.5" />
                    View
                </Button>
            ),
        },
    ];

    return (
        <>
            <div className="min-h-screen bg-gray-50/50 p-6 space-y-8 font-sans">
                {/* Header Section */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
                    <div className="absolute right-0 top-0 opacity-10">
                        <ShieldCheck className="h-48 w-48 -translate-y-8 translate-x-12 transform text-white" />
                    </div>
                    <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
                        <div className="flex items-center gap-4">
                            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                                <FileText className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <h1 className="font-display text-2xl font-bold text-white">
                                    User Audit Logs
                                </h1>
                                <p className="text-white/70">
                                    Track user actions, resource changes, and audit trails across the system.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="outline"
                                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white transition-all backdrop-blur-md"
                                onClick={handleRefresh}
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Sync Data
                            </Button>
                            <Button
                                variant="outline"
                                className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
                                onClick={handleExport}
                                disabled={selectedRows.length === 0}
                            >
                                <Download className="h-4 w-4" />
                                Export
                                {selectedRows.length > 0 && (
                                    <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                                        {selectedRows.length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid gap-6 md:grid-cols-1">
                    <Card className="overflow-hidden border-none shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <p className="text-sm font-medium text-muted-foreground">Total Audit Events</p>
                                    <div className="flex items-baseline gap-2">
                                        <h2 className="text-3xl font-bold text-gray-900">{auditPagination.total}</h2>
                                        <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">All Time</span>
                                    </div>
                                </div>
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content Area */}
                {/* Toolbar & Filters */}
                <div className="border-b p-5 bg-white">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative max-w-sm w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search audit logs..."
                                className="pl-9 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="icon" className="h-10 w-10 text-muted-foreground">
                                <Filter className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                <div className="p-4 bg-gray-50/40 border-b flex justify-between items-center text-xs text-muted-foreground">
                    <span>Showing {auditLogs.length} recent records</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Live</span>
                </div>
                <AntDTable
                    columns={auditColumns}
                    data={auditLogs}
                    loading={loading}
                    showSettings
                    pagination={{
                        current: auditPagination.current,
                        pageSize: auditPagination.pageSize,
                        total: auditPagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: true
                    }}
                    rowKey="id"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: onSelectChange,
                    }}
                    className="border-0"
                    onRow={(record) => ({
                        onClick: () => setSelectedLog(record),
                        className: "cursor-pointer hover:bg-gray-50 transition-colors"
                    })}
                />
            </div>
            <CommonModal
                isOpen={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                width="max-w-4xl w-full"
                height="h-auto"
                padding="p-0"
            >
                <div className="flex flex-col">
                    <div className="border-b rounded-t-full p-6 pb-2">
                        <div className="flex items-center gap-4 mb-2">
                            <Badge
                                variant="outline"
                                className={`uppercase text-xs font-bold px-2 py-1 ${getActionColor(selectedLog?.action)}`}
                            >
                                {selectedLog?.action}
                            </Badge>
                            <h2 className="text-xl font-semibold">
                                {formatResourceType(selectedLog?.resource_type ?? "")}
                            </h2>
                        </div>
                        <p className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                            ID: {selectedLog?.id}
                        </p>
                    </div>

                    <div className="p-6">
                        <div className="space-y-8">
                            {/* User & Request Info */}
                            <div className="grid grid-cols-2 gap-8 text-sm">
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-blue-500" /> User Context
                                    </h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-2">
                                        <span className="text-muted-foreground">User:</span>
                                        <span className="font-medium text-gray-900">
                                            {selectedLog?.user_email}
                                        </span>
                                        <span className="text-muted-foreground">IP Address:</span>
                                        <span className="font-mono text-xs bg-gray-100 px-1 rounded w-fit">
                                            {selectedLog?.ip_address}
                                        </span>
                                        <span className="text-muted-foreground">User Agent:</span>
                                        <span
                                            className="text-xs text-muted-foreground truncate"
                                            title={selectedLog?.user_agent}
                                        >
                                            {selectedLog?.user_agent}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                        <Laptop className="w-4 h-4 text-purple-500" /> System
                                        Context
                                    </h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-2">
                                        <span className="text-muted-foreground">App Name:</span>
                                        <span>{selectedLog?.app_name}</span>
                                        <span className="text-muted-foreground">Request:</span>
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant="secondary"
                                                className="text-[10px] font-mono"
                                            >
                                                {selectedLog?.request_method}
                                            </Badge>
                                            <span
                                                className="font-mono text-xs text-muted-foreground truncate max-w-[200px]"
                                                title={selectedLog?.request_path}
                                            >
                                                {selectedLog?.request_path}
                                            </span>
                                        </div>
                                        <span className="text-muted-foreground">Timestamp:</span>
                                        <span>
                                            {selectedLog && formatDate(selectedLog.created_on)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Data Changes */}
                            <div className="space-y-4">
                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-orange-500" /> Data Changes
                                </h4>

                                {selectedLog?.old_values || selectedLog?.new_values ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Old Values */}
                                        <div className="space-y-2">
                                            <div className="text-xs font-semibold uppercase tracking-wider text-red-600 flex items-center gap-2">
                                                Previous State
                                            </div>
                                            <div className="bg-red-50/50 border border-red-100 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                                                {selectedLog?.old_values ? (
                                                    <pre className="whitespace-pre-wrap">
                                                        {JSON.stringify(
                                                            selectedLog.old_values,
                                                            null,
                                                            2
                                                        )}
                                                    </pre>
                                                ) : (
                                                    <span className="text-gray-400 italic">
                                                        No previous state (Creation)
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* New Values */}
                                        <div className="space-y-2">
                                            <div className="text-xs font-semibold uppercase tracking-wider text-green-600 flex items-center gap-2">
                                                New State
                                            </div>
                                            <div className="bg-green-50/50 border border-green-100 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                                                {selectedLog?.new_values ? (
                                                    <pre className="whitespace-pre-wrap">
                                                        {JSON.stringify(
                                                            selectedLog.new_values,
                                                            null,
                                                            2
                                                        )}
                                                    </pre>
                                                ) : (
                                                    <span className="text-gray-400 italic">
                                                        No new state (Deletion)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 border border-dashed rounded-lg p-8 text-center text-muted-foreground text-sm">
                                        No data changes recorded for this event.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </CommonModal>
        </>
    );
};

export default UserAuditLogs;
